import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, Clock, User, Mail, Phone, MessageSquare, CheckCircle, Info, Banknote, Loader } from 'lucide-react';
import { Calendar } from '../components/ui/calendar';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { SERVICE_PRICES, OPENING_HOURS, generateTimeSlots, RIB } from '../data/reservationConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Reservation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  // Check for service parameter in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const serviceParam = params.get('service');
    if (serviceParam) {
      setSelectedService(decodeURIComponent(serviceParam));
    }
  }, [location]);

  const services = Object.keys(SERVICE_PRICES);

  // Fetch available slots when date changes
  useEffect(() => {
    if (selectedDate && selectedService) {
      fetchAvailability();
    }
  }, [selectedDate, selectedService]);

  const fetchAvailability = async () => {
    try {
      // Générer tous les créneaux selon NOS horaires d'ouverture
      const allSlots = generateTimeSlots(selectedDate);
      
      if (allSlots.length === 0) {
        setAvailableSlots([]);
        return;
      }
      
      // Essayer de récupérer les créneaux déjà réservés depuis le backend
      try {
        const dateStr = selectedDate.toISOString().split('T')[0];
        const response = await axios.get(`${BACKEND_URL}/api/availability/${dateStr}`);
        
        // Filtrer les créneaux déjà réservés
        const bookedSlots = response.data.booked_slots || [];
        const available = allSlots.filter(slot => !bookedSlots.includes(slot));
        
        setAvailableSlots(available);
      } catch (backendError) {
        // Si le backend ne répond pas, afficher quand même tous les créneaux
        console.warn('Backend availability check failed, showing all slots:', backendError);
        setAvailableSlots(allSlots);
      }
      
    } catch (error) {
      console.error('Error generating time slots:', error);
      setAvailableSlots([]);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime || !selectedService) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Veuillez remplir vos coordonnées');
      return;
    }

    setLoading(true);

    try {
      // Create booking
      const bookingData = {
        service: selectedService,
        date: selectedDate.toISOString().split('T')[0],
        time: selectedTime,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message || ''
      };

      const bookingResponse = await axios.post(`${BACKEND_URL}/api/bookings`, bookingData);
      const booking = bookingResponse.data;
      
      // Sauvegarder les infos de prix AVANT de reset
      const priceInfo = SERVICE_PRICES[selectedService];
      booking.priceInfo = priceInfo; // Ajouter les infos de prix au booking
      
      setBookingConfirmed(booking);
      setShowConfirmation(true);
      
      // Scroller en haut de la page
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
      
      toast.success('Réservation confirmée !');
      
      // Réinitialiser le formulaire
      setFormData({ name: '', email: '', phone: '', message: '' });
      setSelectedDate(null);
      setSelectedTime('');
      setSelectedService('');
      
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Erreur lors de la réservation. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const selectedServiceInfo = selectedService ? SERVICE_PRICES[selectedService] : null;

  // Vérifier si le jour sélectionné est ouvert
  const isDayOpen = (date) => {
    const dayOfWeek = date.getDay();
    return OPENING_HOURS[dayOfWeek] && OPENING_HOURS[dayOfWeek].length > 0;
  };

  // Désactiver les jours fermés dans le calendrier
  const disabledDays = (date) => {
    return !isDayOpen(date);
  };

  if (showConfirmation && bookingConfirmed) {
    return (
      <div className="page-reservation">
        <div className="reservation-hero">
          <div className="service-hero-pattern" />
          <div className="reservation-hero-content">
            <h1 className="reservation-hero-title">Réservation Confirmée</h1>
          </div>
        </div>

        <div className="reservation-confirmation-container">
          <div className="confirmation-card">
            <div className="confirmation-icon">
              <CheckCircle size={64} className="text-success" />
            </div>
            
            <h2>Votre réservation a été enregistrée !</h2>
            
            <div className="confirmation-details">
              <h3>Détails de votre réservation :</h3>
              <div className="detail-row">
                <strong>Service :</strong>
                <span>{bookingConfirmed.service}</span>
              </div>
              <div className="detail-row">
                <strong>Date :</strong>
                <span>{new Date(bookingConfirmed.date).toLocaleDateString('fr-FR', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</span>
              </div>
              <div className="detail-row">
                <strong>Heure :</strong>
                <span>{bookingConfirmed.time}</span>
              </div>
              <div className="detail-row">
                <strong>Nom :</strong>
                <span>{bookingConfirmed.name}</span>
              </div>
            </div>

            <div className="payment-info-box">
              <div className="info-header">
                <Banknote size={24} />
                <h3>Informations de Paiement</h3>
              </div>
              
              <div className="price-details">
                <div className="price-row">
                  <span>Prix de la prestation :</span>
                  <strong>{bookingConfirmed.priceInfo?.full || 0}€</strong>
                </div>
                <div className="price-row highlight">
                  <span>Acompte demandé :</span>
                  <strong>{bookingConfirmed.priceInfo?.deposit || 0}€</strong>
                </div>
              </div>

              <div className="payment-methods">
                <h4>Modes de paiement acceptés :</h4>
                
                <div className="payment-method">
                  <strong>💶 Espèces</strong>
                  <p>Vous pouvez régler votre acompte directement au salon lors de votre arrivée.</p>
                </div>

                <div className="payment-method">
                  <strong>🏦 Virement bancaire</strong>
                  <p>Pour régler par virement, utilisez les coordonnées suivantes :</p>
                  <div className="rib-box">
                    <strong>RIB :</strong>
                    <code>{RIB}</code>
                  </div>
                  <p className="payment-note">
                    <Info size={16} />
                    Merci d'indiquer votre nom et la date du rendez-vous dans le libellé du virement.
                  </p>
                </div>
              </div>

              <div className="important-notice">
                <Info size={20} />
                <p>
                  <strong>Important :</strong> Votre réservation sera confirmée définitivement après réception de l'acompte de {bookingConfirmed.priceInfo?.deposit || 0}€. 
                  Le solde de {bookingConfirmed.priceInfo ? bookingConfirmed.priceInfo.full - bookingConfirmed.priceInfo.deposit : 0}€ sera à régler lors de votre visite.
                </p>
              </div>
            </div>

            <div className="confirmation-actions">
              <button 
                onClick={() => {
                  setShowConfirmation(false);
                  setBookingConfirmed(null);
                  window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                }} 
                className="btn-primary"
              >
                Nouvelle Réservation
              </button>
              <button 
                onClick={() => navigate('/')} 
                className="btn-secondary"
              >
                Retour à l'Accueil
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-reservation">
      {/* Hero Section */}
      <div className="reservation-hero">
        <div className="service-hero-pattern" />
        <div className="reservation-hero-content">
          <h1 className="reservation-hero-title">Réserver un Rendez-vous</h1>
          <p className="reservation-hero-description">
            Choisissez votre prestation et prenez rendez-vous en quelques clics
          </p>
        </div>
      </div>

      {/* Booking Form */}
      <section className="reservation-form-section">
        <div className="container-reservation">
          <form onSubmit={handleSubmit} className="reservation-form-card">
            
            {/* Step 1: Service Selection */}
            <div className="form-step">
              <div className="step-header">
                <div className="step-number">1</div>
                <h2>Choisissez votre prestation</h2>
              </div>
              
              <div className="form-group">
                <label htmlFor="service" className="form-label required">
                  Prestation *
                </label>
                <select
                  id="service"
                  value={selectedService}
                  onChange={(e) => setSelectedService(e.target.value)}
                  className="form-select"
                  required
                >
                  <option value="">-- Sélectionnez une prestation --</option>
                  {services.map((service) => (
                    <option key={service} value={service}>
                      {service} - {SERVICE_PRICES[service].full}€
                    </option>
                  ))}
                </select>
                
                {selectedServiceInfo && (
                  <div className="service-price-info">
                    <div className="price-detail">
                      <span>Prix :</span>
                      <strong>{selectedServiceInfo.full}€</strong>
                    </div>
                    <div className="price-detail">
                      <span>Acompte :</span>
                      <strong>{selectedServiceInfo.deposit}€</strong>
                    </div>
                    <div className="price-detail">
                      <span>Première visite :</span>
                      <strong>{selectedServiceInfo.firstVisit}€</strong>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Step 2: Date & Time Selection */}
            {selectedService && (
              <div className="form-step">
                <div className="step-header">
                  <div className="step-number">2</div>
                  <h2>Choisissez la date et l'heure</h2>
                </div>

                <div className="datetime-container">
                  <div className="calendar-wrapper">
                    <label className="form-label required">Date *</label>
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={disabledDays}
                      className="reservation-calendar"
                      fromDate={new Date()}
                    />
                    
                    <div className="opening-hours-info">
                      <h4>Horaires d'ouverture :</h4>
                      <ul>
                        <li>Lundi - Jeudi : 10h-12h et 14h-20h</li>
                        <li>Vendredi : 15h-21h</li>
                        <li>Samedi : 10h-12h et 14h-20h</li>
                        <li>Dimanche : 10h-13h</li>
                      </ul>
                    </div>
                  </div>

                  <div className="time-slots-wrapper">
                    <label className="form-label required">Créneau horaire *</label>
                    {selectedDate ? (
                      availableSlots.length > 0 ? (
                        <div className="time-slots-grid">
                          {availableSlots.map((slot) => (
                            <button
                              key={slot}
                              type="button"
                              onClick={() => setSelectedTime(slot)}
                              className={`time-slot-btn ${selectedTime === slot ? 'selected' : ''}`}
                            >
                              <Clock size={16} />
                              {slot}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="no-slots-message">
                          Aucun créneau disponible pour cette date. Veuillez choisir une autre date.
                        </p>
                      )
                    ) : (
                      <p className="select-date-message">
                        Sélectionnez d'abord une date pour voir les créneaux disponibles
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Contact Information */}
            {selectedService && selectedDate && selectedTime && (
              <div className="form-step">
                <div className="step-header">
                  <div className="step-number">3</div>
                  <h2>Vos coordonnées</h2>
                </div>

                <div className="contact-form-grid">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label required">
                      <User size={18} />
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Votre nom et prénom"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email" className="form-label required">
                      <Mail size={18} />
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="votre@email.com"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone" className="form-label required">
                      <Phone size={18} />
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="06.12.34.56.78"
                      required
                    />
                  </div>

                  <div className="form-group full-width">
                    <label htmlFor="message" className="form-label">
                      <MessageSquare size={18} />
                      Message (optionnel)
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      className="form-textarea"
                      placeholder="Des informations supplémentaires ?"
                      rows="4"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Summary & Submit */}
            {selectedService && selectedDate && selectedTime && formData.name && formData.email && formData.phone && (
              <div className="form-summary">
                <h3>Récapitulatif</h3>
                <div className="summary-grid">
                  <div className="summary-item">
                    <strong>Prestation :</strong>
                    <span>{selectedService}</span>
                  </div>
                  <div className="summary-item">
                    <strong>Date :</strong>
                    <span>{selectedDate.toLocaleDateString('fr-FR', { 
                      weekday: 'long', 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })}</span>
                  </div>
                  <div className="summary-item">
                    <strong>Heure :</strong>
                    <span>{selectedTime}</span>
                  </div>
                  <div className="summary-item">
                    <strong>Nom :</strong>
                    <span>{formData.name}</span>
                  </div>
                  <div className="summary-item price-summary">
                    <strong>Prix total :</strong>
                    <span className="price-amount">{selectedServiceInfo?.full}€</span>
                  </div>
                  <div className="summary-item price-summary">
                    <strong>Acompte à verser :</strong>
                    <span className="price-amount">{selectedServiceInfo?.deposit}€</span>
                  </div>
                </div>

                <p className="form-note">
                  * Champs obligatoires. Un acompte de <strong>{selectedServiceInfo?.deposit}€</strong> sera demandé pour confirmer votre réservation.
                  <br />
                  Vous pourrez régler par espèces ou virement bancaire.
                </p>

                <button 
                  type="submit" 
                  className="btn-submit-reservation"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader className="spinner" size={20} />
                      Réservation en cours...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={20} />
                      Confirmer la réservation
                    </>
                  )}
                </button>
              </div>
            )}
          </form>
        </div>
      </section>
    </div>
  );
};

export default Reservation;
